import {
  UpdateTaskRequest,
  WeightProofVerificationControllerService
} from "@9amhealth/openapi";
import { Blac, Cubit } from "blac-next";
import { KnownGroup, KnownProgram } from "src/constants/programs";
import { dateLocalString } from "src/lib/date";
import { tracker } from "src/state/state";
import TaskManagementBloc, {
  TaskKey,
  TaskObserverEvent
} from "src/state/TaskManagementBloc/TaskManagementBloc";
import { TaskConfigAdditionalData } from "src/ui/components/TaskList/TaskListBloc";
import { AppQueryPopupsController } from "src/ui/components/AppQueryPopups/AppQueryPopupsBloc";
import BlockingLoadingOverlayController from "src/ui/components/BlockingLoadingOverlay/BlockingLoadingOverlayController";

interface WeightProofUploadDialogState {
  uploadedFileIds: string[];
  uploadReady: boolean;
  showSkipConfirm: boolean;
  startDate?: string;
  endDate?: string;
}

type NavigateToReturnUrl = (
  to: string,
  options?: {
    multiBack?: boolean;
    replace?: boolean;
  }
) => void;

const initialState: WeightProofUploadDialogState = {
  uploadedFileIds: [],
  uploadReady: false,
  showSkipConfirm: false,
  startDate: undefined,
  endDate: undefined
};

export default class WeightProofUploadDialogCubit extends Cubit<WeightProofUploadDialogState> {
  private taskManagementBloc = Blac.getBloc(TaskManagementBloc);
  private taskObserver: ReturnType<
    typeof this.taskManagementBloc.addObserver
  > | null = null;

  constructor() {
    super(initialState);
    this.updateDateRange();
    this.taskObserver = this.taskManagementBloc.addObserver(
      TaskObserverEvent.TASK_LIST_CHANGED,
      this.updateDateRange
    );
  }

  private updateDateRange = () => {
    const weightProofTask = this.taskManagementBloc.state.tasks.find(
      (task) => task.slug === TaskKey.UPLOAD_PROOF_OF_WEIGHT
    );
    const additionalData = weightProofTask?.additionalData as
      | TaskConfigAdditionalData
      | undefined;
    const minDate = additionalData?.pre_glp1_weight_proof_min_date;
    const maxDate = additionalData?.pre_glp1_weight_proof_max_date;

    if (!minDate || !maxDate) {
      this.patch({ startDate: undefined, endDate: undefined });
      return;
    }

    this.patch({
      startDate: dateLocalString(minDate, { dateStyle: "long" }),
      endDate: dateLocalString(maxDate, { dateStyle: "long" })
    });
  };

  setUploadedFileIds = (uploadedFileIds: string[]) => {
    this.patch({ uploadedFileIds });
  };

  setUploadReady = (uploadReady: boolean) => {
    this.patch({ uploadReady });
  };

  handleClose = (returnUrl: string, navigate: NavigateToReturnUrl) => {
    AppQueryPopupsController.closePopup();
    navigate(returnUrl, {
      multiBack: true,
      replace: true
    });
  };

  handleSkip = () => {
    tracker.track("Weight Proof Upload Skipped");
    void this.taskManagementBloc
      .updateTaskStatus(
        {
          program: KnownProgram.ONBOARDING,
          group: KnownGroup.GLP1_CONTINUATION,
          slug: TaskKey.UPLOAD_PROOF_OF_WEIGHT
        },
        UpdateTaskRequest.status.SKIPPED
      )
      .then(() => {
        AppQueryPopupsController.closePopup();
      });
  };

  handleContinue = (returnUrl: string, navigate: NavigateToReturnUrl) => {
    BlockingLoadingOverlayController.startLoading({
      message: "weightProofUpload.verify.loading",
      bg: "transparent"
    });

    void WeightProofVerificationControllerService.verify().then((result) => {
      if (result.data.verified) {
        BlockingLoadingOverlayController.loadingSuccess({
          message: "weightProofUpload.verify.success"
        });

        void this.taskManagementBloc
          .updateTaskStatus(
            {
              program: KnownProgram.ONBOARDING,
              group: KnownGroup.GLP1_CONTINUATION,
              slug: TaskKey.UPLOAD_PROOF_OF_WEIGHT
            },
            UpdateTaskRequest.status.COMPLETED
          )
          .then(() => {
            // Close after the success overlay auto-dismisses (message-only = 2000ms)
            setTimeout(() => {
              this.handleClose(returnUrl, navigate);
            }, 2100);
          });
      } else {
        BlockingLoadingOverlayController.loadingError({
          title: null,
          showIcon: true,
          message: result.data.reasonIfNotVerified ?? undefined,
          cancelText: "weightProofUpload.verify.error.backToUpload",
          onCancel: () => BlockingLoadingOverlayController.endLoading(),
          onSkip: this.handleRequestSkip,
          skipText: "weightProofUpload.verify.error.skipForNow"
        });
      }
    });
  };

  handleRequestSkip = () => {
    this.patch({ showSkipConfirm: true });
  };

  handleSkipConfirmClose = () => {
    this.patch({ showSkipConfirm: false });
  };

  _dispose(): void {
    if (this.taskObserver) {
      this.taskObserver();
      this.taskObserver = null;
    }

    super._dispose();
  }
}
